import { useState, type FormEvent } from 'react'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { AvisoError, CabeceraModal, CabeceraPagina, Campo, CLASE_BOTON, CLASE_BOTON_PRIMARIO, CLASE_INPUT, Insignia } from '../componentes/formulario'
import { IconoAnadir, IconoClave, IconoEditar } from '../componentes/iconos'
import { Modal } from '../componentes/Modal'
import { SkeletonList } from '../componentes/Skeleton'
import { SoloAdmin } from '../componentes/SoloAdmin'
import type { RolUsuario, UsuarioPanel } from '../api/modelos'
import { useCrearUsuario, useEditarUsuario, useRestablecerPassword, useUsuarios } from '../hooks/useUsuarios'
import { useUsuarioActual } from '../hooks/useUsuarioActual'
import { BarraPaginacion } from '../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../componentes/paginacion'

const ROLES: { valor: RolUsuario; etiqueta: string }[] = [
  { valor: 'usuario', etiqueta: 'Operador' },
  { valor: 'admin', etiqueta: 'Administrador' },
]

function SelectRol({ valor, onCambiar, deshabilitado }: { valor: RolUsuario; onCambiar: (r: RolUsuario) => void; deshabilitado?: boolean }) {
  return (
    <select value={valor} onChange={(e) => onCambiar(e.target.value as RolUsuario)} disabled={deshabilitado} className={CLASE_INPUT}>
      {ROLES.map((r) => <option key={r.valor} value={r.valor}>{r.etiqueta}</option>)}
    </select>
  )
}

function AltaUsuario({ onCerrar }: { onCerrar: () => void }) {
  const crear = useCrearUsuario()
  const [v, setV] = useState({ email: '', nombre: '', rol: 'usuario' as RolUsuario, password: '' })
  const completo = v.email.trim() && v.nombre.trim() && v.password
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    if (completo) crear.mutate({ ...v, email: v.email.trim(), nombre: v.nombre.trim() }, { onSuccess: onCerrar })
  }
  return (
    <Modal titulo="Nuevo usuario" onCerrar={onCerrar}>
      <CabeceraModal titulo="Nuevo usuario" onCerrar={onCerrar} />
      <form onSubmit={enviar} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
        <Campo etiqueta="Correo *"><input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} className={CLASE_INPUT} autoComplete="off" /></Campo>
        <Campo etiqueta="Nombre *"><input value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} className={CLASE_INPUT} /></Campo>
        <Campo etiqueta="Rol"><SelectRol valor={v.rol} onCambiar={(rol) => setV({ ...v, rol })} /></Campo>
        <Campo etiqueta="Contraseña inicial *" ayuda="Compártela por un canal seguro; el usuario puede pedir que se la cambies.">
          <input type="password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} className={CLASE_INPUT} autoComplete="new-password" />
        </Campo>
        <AvisoError error={crear.error} />
        <div className="flex gap-2">
          <button type="submit" disabled={!completo || crear.isPending} className={CLASE_BOTON_PRIMARIO}>{crear.isPending ? 'Creando' : 'Crear usuario'}</button>
          <button type="button" onClick={onCerrar} className={CLASE_BOTON}>Cancelar</button>
        </div>
      </form>
    </Modal>
  )
}

function EditarUsuario({ usuario, esYo, onCerrar }: { usuario: UsuarioPanel; esYo: boolean; onCerrar: () => void }) {
  const editar = useEditarUsuario()
  const [nombre, setNombre] = useState(usuario.nombre)
  const [rol, setRol] = useState<RolUsuario>(usuario.rol)
  const titulo = `Editar ${usuario.email}`
  const enviar = (e: FormEvent) => {
    e.preventDefault()
    const datos: { nombre?: string; rol?: RolUsuario } = {}
    if (nombre.trim() !== usuario.nombre) datos.nombre = nombre.trim()
    if (rol !== usuario.rol) datos.rol = rol
    if (Object.keys(datos).length === 0) return onCerrar()
    editar.mutate({ id: usuario.id, datos }, { onSuccess: onCerrar })
  }
  return (
    <Modal titulo={titulo} onCerrar={onCerrar}>
      <CabeceraModal titulo={titulo} onCerrar={onCerrar} />
      <form onSubmit={enviar} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
        <Campo etiqueta="Nombre"><input value={nombre} onChange={(e) => setNombre(e.target.value)} className={CLASE_INPUT} /></Campo>
        <Campo etiqueta="Rol" ayuda={esYo ? 'No puedes quitarte a ti mismo el rol de administrador.' : undefined}>
          <SelectRol valor={rol} onCambiar={setRol} deshabilitado={esYo && usuario.rol === 'admin'} />
        </Campo>
        <AvisoError error={editar.error} />
        <div className="flex gap-2">
          <button type="submit" disabled={editar.isPending || !nombre.trim()} className={CLASE_BOTON_PRIMARIO}>{editar.isPending ? 'Guardando' : 'Guardar cambios'}</button>
          <button type="button" onClick={onCerrar} className={CLASE_BOTON}>Cancelar</button>
        </div>
      </form>
    </Modal>
  )
}

function Restablecer({ usuario, onCerrar }: { usuario: UsuarioPanel; onCerrar: () => void }) {
  const restablecer = useRestablecerPassword()
  const [password, setPassword] = useState('')
  const titulo = `Nueva contraseña para ${usuario.email}`
  return (
    <Modal titulo={titulo} onCerrar={onCerrar}>
      <CabeceraModal titulo={titulo} onCerrar={onCerrar} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (password) restablecer.mutate({ id: usuario.id, password })
        }}
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4"
      >
        <Campo etiqueta="Nueva contraseña *">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={CLASE_INPUT} autoComplete="new-password" />
        </Campo>
        <AvisoError error={restablecer.error} />
        {restablecer.isSuccess ? <p role="status" className="text-sm text-positivo-tinta">Contraseña cambiada.</p> : null}
        <div className="flex gap-2">
          {!restablecer.isSuccess ? (
            <button type="submit" disabled={!password || restablecer.isPending} className={CLASE_BOTON_PRIMARIO}>
              {restablecer.isPending ? 'Guardando' : 'Cambiar contraseña'}
            </button>
          ) : null}
          <button type="button" onClick={onCerrar} className={CLASE_BOTON}>{restablecer.isSuccess ? 'Cerrar' : 'Cancelar'}</button>
        </div>
      </form>
    </Modal>
  )
}

function ListaUsuarios() {
  const usuarios = useUsuarios()
  const yo = useUsuarioActual().data
  const activar = useEditarUsuario()
  const [modal, setModal] = useState<{ tipo: 'alta' } | { tipo: 'editar' | 'clave'; usuario: UsuarioPanel } | null>(null)
  const lista = usuarios.data ?? []
  const pag = usePaginacionLocal(lista)

  return (
    <div className="flex flex-col gap-4">
      <CabeceraPagina
        titulo="Usuarios"
        descripcion="Usuarios con acceso local al panel. Un usuario no se borra: se desactiva. Siempre tiene que quedar un administrador activo."
        acciones={
          <button type="button" className={CLASE_BOTON_PRIMARIO} onClick={() => setModal({ tipo: 'alta' })}>
            <IconoAnadir aria-hidden="true" className="size-4" />
            Nuevo usuario
          </button>
        }
      />
      <AvisoError error={activar.error} />
      {usuarios.isPending ? (
        <SkeletonList />
      ) : usuarios.isError ? (
        <EstadoError error={usuarios.error} onReintentar={() => void usuarios.refetch()} />
      ) : lista.length === 0 ? (
        <EstadoVacio titulo="No hay usuarios locales" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-borde text-xs uppercase tracking-wide text-tinta-apagada">
              <tr>
                <th className="px-4 py-2 font-semibold">Usuario</th>
                <th className="px-4 py-2 font-semibold">Rol</th>
                <th className="px-4 py-2 font-semibold">Estado</th>
                <th className="px-4 py-2 font-semibold">Último acceso</th>
                <th className="px-4 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borde-suave">
              {pag.visibles.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-2">
                    <p className="font-medium text-tinta">{u.nombre}</p>
                    <p className="text-xs text-tinta-apagada">{u.email}</p>
                  </td>
                  <td className="px-4 py-2 text-tinta-2">{ROLES.find((r) => r.valor === u.rol)?.etiqueta ?? u.rol}</td>
                  <td className="px-4 py-2">{u.activo ? <Insignia tono="positivo">Activo</Insignia> : <Insignia tono="neutro">Desactivado</Insignia>}</td>
                  <td className="px-4 py-2 text-tinta-2">
                    {u.ultimoAcceso ? new Date(u.ultimoAcceso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : 'Nunca'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <button type="button" className={`${CLASE_BOTON} mr-2`} onClick={() => setModal({ tipo: 'editar', usuario: u })} aria-label={`Editar ${u.email}`}>
                      <IconoEditar aria-hidden="true" className="size-4" />
                    </button>
                    <button type="button" className={`${CLASE_BOTON} mr-2`} onClick={() => setModal({ tipo: 'clave', usuario: u })} aria-label={`Restablecer la contraseña de ${u.email}`}>
                      <IconoClave aria-hidden="true" className="size-4" />
                    </button>
                    <button
                      type="button"
                      className={CLASE_BOTON}
                      disabled={activar.isPending || (u.activo && u.email === yo?.email)}
                      onClick={() => activar.mutate({ id: u.id, datos: { activo: !u.activo } })}
                      aria-label={`${u.activo ? 'Desactivar' : 'Activar'} ${u.email}`}
                    >
                      {u.activo ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} />

      {modal?.tipo === 'alta' ? <AltaUsuario onCerrar={() => setModal(null)} /> : null}
      {modal?.tipo === 'editar' ? (
        <EditarUsuario usuario={modal.usuario} esYo={modal.usuario.email === yo?.email} onCerrar={() => setModal(null)} />
      ) : null}
      {modal?.tipo === 'clave' ? <Restablecer usuario={modal.usuario} onCerrar={() => setModal(null)} /> : null}
    </div>
  )
}

/** Usuarios (RF-46), solo para administradores. */
export function Usuarios() {
  return (
    <SoloAdmin>
      <ListaUsuarios />
    </SoloAdmin>
  )
}
